import type { LiveCall } from '../../api/types';

/**
 * A ringing call as the overlay holds it.
 *
 * `call.ringing` arrives in TWO shapes, one per voice channel, and neither is
 * going to grow into the other because they are different providers' ids:
 * RingCentral sends an enriched telephony session (`telephonySessionId`, the
 * caller already resolved to a contact), Twilio sends its own webhook shape
 * keyed on `callSid`. The overlay normalizes both into this, so the card and
 * everything downstream never learns which webhook it came from — and, more to
 * the point, a Twilio call raises a card at all. The old `telephonySessionId`
 * guard silently dropped every one of them, which is why the only place a
 * Twilio call could be answered was the call bar.
 */
export interface RingCall {
  /** What a card is addressed by: RC's session id, or Twilio's call sid. */
  key: string;
  provider: 'ringcentral' | 'twilio';
  direction: string;
  from: string | null;
  to: string | null;
  inboxId: number | null;
  /** Only RingCentral enriches these; Twilio's card resolves them locally. */
  inboxName: string | null;
  contactName: string | null;
  conversationDisplayId: number | null;
}

type RawRing = Partial<LiveCall> & {
  provider?: unknown;
  callSid?: unknown;
};

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

const num = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/**
 * The id both shapes are keyed on, or null when the payload names no call.
 * `call.answered` / `call.ended` clear a card by this, so it is read from the
 * terminal events too — which carry the id and nothing else worth having.
 */
export function ringKey(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const raw = payload as RawRing;
  return str(raw.telephonySessionId) ?? str(raw.callSid);
}

/** One `call.ringing` payload, whichever channel sent it, or null. */
export function normalizeRing(payload: unknown): RingCall | null {
  const key = ringKey(payload);
  if (!key) return null;
  const raw = payload as RawRing;
  // RingCentral's payload names no provider at all — the session id is what
  // identifies it — so the id that IS present decides.
  const provider =
    str(raw.provider) === 'twilio' || (!str(raw.telephonySessionId) && str(raw.callSid))
      ? 'twilio'
      : 'ringcentral';
  return {
    key,
    provider,
    direction: str(raw.direction) ?? '',
    from: str(raw.from),
    to: str(raw.to),
    inboxId: num(raw.inboxId),
    inboxName: str(raw.inboxName),
    contactName: str(raw.contactName),
    conversationDisplayId: num(raw.conversationDisplayId),
  };
}

/** Digits only, so `+1 212 555 0100` and `+12125550100` are one number. */
const digits = (value: string | null): string => (value ?? '').replace(/\D/g, '');

/**
 * What this browser can do about one ringing card.
 *
 * The card is raised from the SERVER's view of the call — an account-wide
 * webhook — while answering happens on a phone in this tab. Those are not the
 * same thing: a call routed to a queue, to somebody else's extension, or to an
 * inbox this browser holds no phone for rings here not at all. So the card
 * works out up front whether it can help, rather than offering a button that
 * can only fail:
 *
 * - `ready`     — a phone in this tab is holding this call. Answer and Ignore.
 * - `waiting`   — a phone that should ring for it has not yet; the socket and
 *                 the phone's own invite race by a beat and the agent is
 *                 looking at the card either way.
 * - `elsewhere` — nothing here is going to ring it. Say which device will.
 */
export type RingReach =
  | { state: 'ready'; via: 'twilio' | 'ringcentral'; ignoreHint: string }
  | { state: 'waiting'; label: string }
  | { state: 'elsewhere'; label: string; openDialer: boolean };

export interface ReachInputs {
  ring: RingCall;
  /** The Twilio softphone, or null when the module has none registered. */
  phone: { inboxId: number | null; ringing: boolean; peer: string | null } | null;
  /** The RingCentral dialer, or null when this console mounts none. */
  dialer: { registered: boolean; ringing: boolean } | null;
  /** True once the grace window for the phone's own invite has passed. */
  waited: boolean;
  /** The only card on screen — so the only call a lone ring can mean. */
  sole: boolean;
}

/** Ignore, not Decline: neither phone can hang up on the caller. See docs. */
const IGNORE_BROWSER = 'Stops it ringing in this browser. The call keeps ringing on your other devices.';

export function ringReach({ ring, phone, dialer, waited, sole }: ReachInputs): RingReach {
  if (ring.provider === 'twilio') {
    const registered = phone !== null && phone.inboxId !== null && phone.inboxId === ring.inboxId;
    if (!registered) {
      return {
        state: 'elsewhere',
        label: 'Answer on the device this number rings.',
        openDialer: false,
      };
    }
    // The device names the caller, not the call, so a second ring on the same
    // number cannot be told apart — the sole card is then the only one it can
    // mean, and matching numbers settle it when there are several.
    const holdsThisCall = phone.ringing && (sole || digits(phone.peer) === digits(ring.from));
    if (holdsThisCall) return { state: 'ready', via: 'twilio', ignoreHint: IGNORE_BROWSER };
    return waited
      ? {
          state: 'elsewhere',
          label: "This call isn't ringing on this browser — answer it where it is.",
          openDialer: false,
        }
      : { state: 'waiting', label: 'Ringing your browser phone…' };
  }

  if (!dialer) {
    return {
      state: 'elsewhere',
      label: 'Answer on your RingCentral device.',
      openDialer: false,
    };
  }
  if (!dialer.registered) {
    return {
      state: 'elsewhere',
      label: 'Answer on your RingCentral device, or sign the dialer in to answer here.',
      openDialer: true,
    };
  }
  if (dialer.ringing) return { state: 'ready', via: 'ringcentral', ignoreHint: IGNORE_BROWSER };
  return waited
    ? {
        // The card is raised for every call on the number, including ones
        // RingCentral routed to a queue or another extension. Only the device
        // it is actually ringing can answer or silence those.
        state: 'elsewhere',
        label: "This call isn't ringing on this browser — use the device it is ringing on.",
        openDialer: true,
      }
    : { state: 'waiting', label: 'Ringing your dialer…' };
}
